import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type HardwareBulkSyncFileCrcCheckedEventProps,
  type HardwareFileDecryptionFailedEventProps,
  type HardwareFileDecryptionSkippedEventProps,
  type HardwareFileDecryptionSuccessEventProps,
  type HardwareSessionCreatedEventProps,
} from '@/models/analytics/features/chronicle';
import { isDeviceEventEnabled } from '@/utils/analytics/track-remote-device-events';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import {
  trackChronicleDeviceConnectedSpec,
  trackChronicleDeviceDisconnectedSpec,
  trackChronicleDeviceSearchStartedSpec,
  trackChronicleFirmwareUpgradeCompletedSpec,
  trackChronicleFirmwareUpgradeFailedSpec,
  trackChronicleFirmwareUpgradeStartedSpec,
  trackDeviceEventSpec,
  trackHardwareDeviceLinkedSpec,
  trackHardwareDeviceRemovedSpec,
  trackHardwareRecordingStartSpec,
  trackHardwareRecordingStopSpec,
  type ChronicleDeviceConnected,
  type ChronicleDeviceDisconnected,
  type ChronicleDeviceSearchStarted,
  type ChronicleFirmwareUpgradeCompleted,
  type ChronicleFirmwareUpgradeFailed,
  type ChronicleFirmwareUpgradeStarted,
  type DeviceEvent,
  type HardwareDeviceLinked,
  type HardwareDeviceRemoved,
  type HardwareRecordingStart,
  type HardwareRecordingStop,
} from '@snowtype/snowplow';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

// Snowplow specs are still registered under the old `chronicle_*` /
// `chronicle_firmware_*` names in @snowtype/snowplow. The data-team rename
// follow-up updates the generated package; until then we keep the old spec
// calls here and only rename the TrackingEventName keys (Amplitude + Datadog
// see the new names immediately).
export const chronicleEvents: SnowplowEventMap = {
  [TrackingEventName.HardwareDeviceScanStarted]: (props) =>
    trackChronicleDeviceSearchStartedSpec(props as ChronicleDeviceSearchStarted),
  [TrackingEventName.HardwareDeviceConnected]: (props) =>
    trackChronicleDeviceConnectedSpec(props as ChronicleDeviceConnected),
  [TrackingEventName.HardwareDeviceDisconnected]: (props) =>
    trackChronicleDeviceDisconnectedSpec(props as ChronicleDeviceDisconnected),
  [TrackingEventName.NativeFirmwareUpgradeComplete]: (props) =>
    trackChronicleFirmwareUpgradeCompletedSpec(
      props as ChronicleFirmwareUpgradeCompleted
    ),
  [TrackingEventName.NativeFirmwareUpgradeFailed]: (props) =>
    trackChronicleFirmwareUpgradeFailedSpec(props as ChronicleFirmwareUpgradeFailed),
  [TrackingEventName.NativeFirmwareUpgradeStart]: (props) =>
    trackChronicleFirmwareUpgradeStartedSpec(props as ChronicleFirmwareUpgradeStarted),
  [TrackingEventName.HardwareDeviceLinked]: (props) =>
    trackHardwareDeviceLinkedSpec(props as HardwareDeviceLinked),
  [TrackingEventName.HardwareDeviceRemoved]: (props) =>
    trackHardwareDeviceRemovedSpec(props as HardwareDeviceRemoved),
  [TrackingEventName.HardwareRecordingStart]: (props) =>
    trackHardwareRecordingStartSpec(props as HardwareRecordingStart),
  [TrackingEventName.HardwareRecordingStop]: (props) =>
    trackHardwareRecordingStopSpec(props as HardwareRecordingStop),
  // Remote-hardware tracing events — hand-rolled self-describing
  // calls, same reason as the patient_summary_email_* events in patients.ts
  // (the checked-in Snowtype output can't be regenerated here; console API
  // credentials live in 1Password / CI). TODO(snowtype): once
  // snowtype/snowplow.ts is regenerated with console creds, swap these to the
  // generated Spec functions. The URIs must match
  // data-structures/com.heidi/hardware_session_created.yaml and
  // hardware_file_decryption_{skipped,success,failed}.yaml exactly.
  [TrackingEventName.HardwareSessionCreated]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/hardware_session_created/jsonschema/1-0-0',
        data: props as HardwareSessionCreatedEventProps,
      },
    }),
  [TrackingEventName.HardwareFileDecryptionSkipped]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/hardware_file_decryption_skipped/jsonschema/1-0-0',
        data: props as HardwareFileDecryptionSkippedEventProps,
      },
    }),
  [TrackingEventName.HardwareFileDecryptionSuccess]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/hardware_file_decryption_success/jsonschema/1-0-0',
        data: props as HardwareFileDecryptionSuccessEventProps,
      },
    }),
  [TrackingEventName.HardwareFileDecryptionFailed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/hardware_file_decryption_failed/jsonschema/1-0-0',
        data: props as HardwareFileDecryptionFailedEventProps,
      },
    }),
  [TrackingEventName.HardwareBulkSyncFileCrcChecked]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: 'iglu:com.heidi/hardware_bulk_sync_file_crc_checked/jsonschema/1-0-0',
        data: props as HardwareBulkSyncFileCrcCheckedEventProps,
      },
    }),
  [TrackingEventName.DeviceEvent]: (props) => {
    if (!isDeviceEventEnabled()) return;
    trackDeviceEventSpec(props as DeviceEvent);
  },
};
